import { supabase } from "../supabaseClient"

const platformState = (configured, monitored) => {
  if (monitored) return "monitored"
  if (configured) return "configured"
  return "not_configured"
}

export const monitoringReadiness = (row) => {
  if (!row.monitoring_available) return "unavailable"
  if (!Number(row.eligible_platform_count)) return "not_configured"
  if (!row.baseline_approved_at) return "needs_setup"
  if (!row.monitoring_enabled) return "paused"
  return "active"
}

export const normalizeAccountOverviewRow = (row) => ({
  accountId: row.account_id,
  name: row.account_name,
  profile: row.monitoring_profile || "custom_baseline",
  platforms: {
    ga4: platformState(row.ga4_configured, row.ga4_monitored),
    googleAds: platformState(row.google_ads_configured, row.google_ads_monitored),
    meta: platformState(row.meta_configured, row.meta_monitored),
  },
  monitoring: {
    readiness: monitoringReadiness(row),
    frequency: row.monitoring_frequency || "daily",
    monitoredPlatformCount: Number(row.monitored_platform_count || 0),
    eligiblePlatformCount: Number(row.eligible_platform_count || 0),
    health: row.health_status || "unknown",
    openIssueCount: Number(row.open_issue_count || 0),
    lastSuccessfulScanAt: row.last_successful_scan_at || null,
    nextScanAt: row.next_scan_at || null,
  },
})

export const summarizeMeasurementDocumentation = (account, mappings = [], revisions = []) => {
  if (account.profile === "automotive_asc") {
    return { status: "not_applicable", mappedDomainCount: 0, currentDomainCount: 0 }
  }

  const accountMappings = mappings.filter((mapping) =>
    mapping.account_id === account.accountId && mapping.active !== false
  )
  if (!accountMappings.length) {
    return { status: "not_mapped", mappedDomainCount: 0, currentDomainCount: 0 }
  }

  const revisionsByMapping = new Map()
  for (const revision of revisions) {
    const current = revisionsByMapping.get(revision.mapping_id)
    if (!current || Number(revision.version || 0) > Number(current.version || 0)) {
      revisionsByMapping.set(revision.mapping_id, revision)
    }
  }
  const currentDomainCount = accountMappings.filter((mapping) =>
    revisionsByMapping.get(mapping.id)?.status === "published"
  ).length

  return {
    status: currentDomainCount === accountMappings.length ? "current" : "needs_review",
    mappedDomainCount: accountMappings.length,
    currentDomainCount,
  }
}

export async function fetchAccountOverview(organizationId) {
  const [overviewResult, mappingsResult, revisionsResult] = await Promise.all([
    supabase.rpc("get_account_overview", { p_organization_id: organizationId }),
    supabase.from("domain_measurement_mappings")
      .select("id, account_id, active")
      .eq("organization_id", organizationId),
    supabase.from("measurement_document_revisions")
      .select("mapping_id, version, status")
      .eq("organization_id", organizationId),
  ])
  if (overviewResult.error) throw overviewResult.error
  if (mappingsResult.error) throw mappingsResult.error
  if (revisionsResult.error) throw revisionsResult.error

  return (overviewResult.data || []).map((row) => {
    const account = normalizeAccountOverviewRow(row)
    return {
      ...account,
      measurementDocumentation: summarizeMeasurementDocumentation(
        account,
        mappingsResult.data || [],
        revisionsResult.data || [],
      ),
    }
  })
}
